import 'reflect-metadata';
import './fix'
import { createApp } from 'vue'
import router from './routers'
import App from './App.vue'
import './assets/style/main.scss'
import '@patsnap-biz/design-tokens/scss/_variables-RD.css';
import './assets/style/el-overwrites.scss'
import { pwaService } from '@eureka/utils/pwa';
import { startup } from '@eureka/startup'
import { i18n, loadI18n } from '@/infrastructure/locales'
import { container } from './inversify.config';
import { setContainer, injectService } from '@patsnap/tahiti-core/ioc/inject';
import { HeaderServiceProxy } from './services/header-service-proxy'
import { ErrorHandler } from '@patsnap/tahiti-core/common/error-handler';
import { ErrorHandlerRegistry } from '@patsnap/tahiti-core/common/error-handler-contribution';
import { AnonymousError } from '@eureka/services/src/exception';
import { accountDeferred, isUserLogin } from '@eureka/utils/account';
import { LoginDialogService } from './domains/shared/services/login-dialog-service';
import { injectFingerprintInterceptor } from '@eureka/services/src/apis/interceptors/fingerprint.interceptor';
import { SafeHtmlPlugin } from '@eureka/shared/safe-html-plugin';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { AgentSettingsApi } from '@/domains/agent/apis/agent-settings-api';
import { clientDeferred } from '@eureka/utils/client';
import { GtmPlugin } from '@eureka/shared/gtm';
import { initCookieService } from '@eureka/utils/init-cookie';
import { REGION } from '@eureka/utils/environment';
import { DeployEnv } from '@patsnap-utils/biz';

// 配置本地开发环境的 Site href 生成逻辑
// 在本地开发时（localhost），使用相对路径而不是完整的 stage 域名
// if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
//     (window as any)._custom_site_href = function(site: any) {
//         // 只处理 EUREKA 相关的链接
//         if (site.sld === 'eureka') {
//             return `/${site.pathname || ''}`;
//         }
//         // 其他链接使用默认行为（完整域名）
//         return null;
//     };
// }

pwaService.init();
initCookieService.init();
setContainer(container);
injectService<ErrorHandler>(ErrorHandler).onStart();
injectService<ErrorHandlerRegistry>(ErrorHandlerRegistry).onStart();
injectFingerprintInterceptor();

const headerServiceInstance = injectService<HeaderServiceProxy>(HeaderServiceProxy).headerService as HeaderServiceProxy['headerService'];

// 配置数组,可以根据需要进行修改
const detailPageRegExps = [
    /^\/$/,
    /\/home/,
    /\/ai-search/,
    /\/rd/,
    /\/ip/,
    /\/mse/,
    /\/ls/,
    /\/ai-ls\/chat-agent-share\/.*$/,
    /\/ai-ls\/document-agent-share\/.*$/,
    /\/ai\/agent/,
    /^.*\/page\/[^/]+$/,
    /^.*\/ai\/agent-flow\/[^/]+$/,
    /\/ai-search\/.*$/,
    /^\/user-setting\/package$/,
    /^\/customize-list$/,
    /^\/agent-list$/
];

// CN 环境下需要排除的路由（命中这些路由时不视为详情页）
const cnExcludedRegExps = [
    /\/home/
];

// 判断是否为详情页面
const isSeekDetailPage = () => {
    const currentPath = window.location.pathname;
    const isDetailPage = detailPageRegExps.some(pathReg => pathReg.test(currentPath));
    if (isDetailPage && REGION === DeployEnv.CN) {
        return !cnExcludedRegExps.some(pathReg => pathReg.test(currentPath));
    }
    return isDetailPage;
};

const loginDialogService = injectService<LoginDialogService>(LoginDialogService)

startup({
    headerService: headerServiceInstance,
    projectName: 'eureka',
    preset: {
        name: 'eureka',
        preRedirectLogin() {
            const isSkipPage = isSeekDetailPage() && !headerServiceInstance?.userCenterService?.logged
            if (!isSkipPage) {
                loginDialogService.open(false, undefined, 'loginRequired')
            }
            throw new AnonymousError();
        },
    },
    pageInit() {
        return Promise.resolve().then(() => {
            window.hidePageLoading?.();
        })
    },
    customLinksOptions: {
        checkAgentSettingsPermission: async() => {
            try {
                // 优化：游客模式下直接返回 false，避免发起 /eureka/ces/permission 的 401 请求
                const isLogin = await isUserLogin();
                if (!isLogin) {
                    console.debug('[Main] Guest mode detected, skipping checkCesPermission API call');
                    return false;
                }

                const agentSettingsApi = injectService<AgentSettingsApi>(AgentSettingsApi);
                return await agentSettingsApi.checkCesPermission();
            } catch (error) {
                console.error('Failed to check agent settings permission:', error);
                return false;
            }
        },
    },
});

(async() => {
    // 性能优化: 不等待 i18n 加载,立即挂载应用
    // i18n 配置了 missing handler,在语言包加载前不会显示 key
    console.debug('[Main] Starting application mount (i18n lazy loading)');
    const mountStartTime = performance.now();

    // 立即创建和挂载应用
    const app = createApp(App);
    app.config.throwUnhandledErrorInProduction = true;
    app.use(router).use(i18n).use(SafeHtmlPlugin).use(GtmPlugin, router).use(VueQueryPlugin).mount('#app');

    const mountDuration = performance.now() - mountStartTime;
    console.debug(`[Main] Application mounted in ${mountDuration.toFixed(2)}ms`);

    // 后台异步加载 i18n 语言包
    loadI18n().then(() => {
        console.debug('[Main] i18n loaded, translations now available');
    }).catch((error) => {
        console.error('[Main] Failed to load i18n:', error);
    });

    // 后台加载帮助中心(非关键功能)
    accountDeferred.promise.then(
        async() => {
            const { isFeiShu } = await clientDeferred.promise
            if (isFeiShu) return;
            import('@eureka/startup/src/help-center').then(({ getHelpCenter }) => {
                getHelpCenter(i18n.global, headerServiceInstance)
            })
        }
    );
})()
